"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCacheEmptySingleton = getCacheEmptySingleton;
exports.useApiRead = useApiRead;
exports.mkCacheUnit = mkCacheUnit;
exports.useApiReadCache = useApiReadCache;
exports.useApiReadIterCache = useApiReadIterCache;
exports.useApiReadIter = useApiReadIter;
exports.useApiDoBase = useApiDoBase;
const api = __importStar(require("ego-sdk-js"));
const react_1 = __importDefault(require("react"));
const ApiData = __importStar(require("../api-data"));
// Declared top-level to ensure there's only one reference/version of it.
// This is important for maintaining a stable identity.
const CACHE_EMPTY = { ts: 0, value: { kind: 'unknown' } };
function getCacheEmptySingleton() {
    return CACHE_EMPTY;
}
function isCacheExpired(cacheUnit, ttl) {
    if (ttl === undefined) {
        return false;
    }
    return Date.now() - cacheUnit.ts >= ttl * 1000;
}
function useApiRead(apiClient, routeMethod, arg, getValueFromResult, disabled, handlers) {
    const [value, setValue] = react_1.default.useState({ ts: 0, value: { kind: 'unknown' } });
    return useApiReadCache(apiClient, routeMethod, arg, getValueFromResult, (newValue) => setValue(newValue), () => value, disabled, handlers);
}
function mkCacheUnit(value, ts) {
    return {
        ts: ts !== null && ts !== void 0 ? ts : Date.now(),
        value,
    };
}
/**
 * For efficiently retrieving API data.
 *
 * @param arg: Must be weakly "immutable". That is, the serialized value of it
 *   must not change.
 * @param cacheGetSelector **IMPORTANT**: Assumes that this is a hook that will
 *   trigger a re-render of this hook if the value changes.
 * @param cacheTtl In seconds. If unset, ttl is infinite.
 * @param cacheIgnoreStale Set to true to ignore currently cached value.
 * @returns
 */
function useApiReadCache(apiClient, routeMethod, arg, getValueFromResult, cacheSet, cacheGetSelector, disabled, handlers, cacheTtl, cacheIgnoreStale) {
    const initTs = react_1.default.useRef(Date.now());
    const currentCacheUnit = cacheGetSelector();
    // This is necessary to know the instant value of the cache. The version in
    // the state variable can be stale when calling query() because query()'s
    // closure captures it.
    const curRef = react_1.default.useRef(currentCacheUnit.value);
    react_1.default.useEffect(() => {
        // Reflect external changes to cache state (data mutation / override) back
        // to instant cache state.
        if (ApiData.hasData(currentCacheUnit.value) && ApiData.hasData(curRef.current)) {
            curRef.current = currentCacheUnit.value;
        }
    }, [currentCacheUnit]);
    const apiClientAbortable = react_1.default.useRef();
    const query = () => {
        if (ApiData.isLoading(curRef.current)) {
            return;
        }
        cacheSet(mkCacheUnit(ApiData.toLoading(curRef.current), currentCacheUnit.ts));
        curRef.current = ApiData.toLoading(curRef.current);
        apiClientAbortable.current = apiClient.abortable();
        routeMethod
            // The benefit of the late bind is that the method can be bound to an
            // abortable version of the client.
            .bind(apiClientAbortable.current)(arg)
            .then(resp => {
            if (resp.kind === api.StatusCode.Ok) {
                // Call onResult first so that it's completed before the cache value
                // is set. This is important if the next render depends on state set
                // by onResult.
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onResult) {
                    handlers.onResult(resp.result);
                }
                curRef.current = {
                    data: getValueFromResult(resp.result),
                    kind: 'loaded',
                };
                cacheSet(mkCacheUnit(curRef.current));
            }
            else if (resp.kind === api.StatusCode.Error) {
                curRef.current = { kind: 'error', error: resp.error };
                cacheSet(mkCacheUnit(curRef.current));
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onRouteErr) {
                    handlers.onRouteErr(resp.error);
                }
            }
            else {
                curRef.current = { kind: 'error' };
                cacheSet(mkCacheUnit(curRef.current));
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onRespErr) {
                    handlers.onRespErr(resp);
                }
            }
        })
            .catch(e => {
            if (e.name !== 'AbortError') {
                curRef.current = { kind: 'error' };
                cacheSet(mkCacheUnit(curRef.current));
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onHttpErr) {
                    handlers.onHttpErr();
                }
            }
        });
        if (handlers === null || handlers === void 0 ? void 0 : handlers.onQuery) {
            handlers === null || handlers === void 0 ? void 0 : handlers.onQuery();
        }
        return () => {
            apiClientAbortable.current.abort();
        };
    };
    react_1.default.useEffect(() => {
        if (disabled) {
            return;
        }
        else if (currentCacheUnit.value.kind === 'unknown' || isCacheExpired(currentCacheUnit, cacheTtl)) {
            query();
        }
    }, [disabled]);
    const argSet = react_1.default.useRef(null);
    react_1.default.useEffect(() => {
        if (disabled) {
            return;
        }
        // There are too many edge cases and undefined behaviors to handle if `arg`
        // is allowed to change. Simply reject it.
        // NOTE: react hot-reload re-invokes this even if arg hasn't changed, which
        // is why an explicit check against the known arg is required.
        const serializedArg = JSON.stringify(arg);
        if (argSet.current === null) {
            argSet.current = serializedArg;
        }
        else if (argSet.current !== serializedArg) {
            throw Error('Unexpected change to arg.');
        }
    }, [JSON.stringify(arg)]);
    // This hides stale data if the caller requires it.
    let staleAwareValue = currentCacheUnit;
    if (cacheIgnoreStale && isCacheExpired(currentCacheUnit, cacheTtl) && currentCacheUnit.ts < initTs.current) {
        if (ApiData.isLoading(currentCacheUnit.value)) {
            staleAwareValue = { ts: currentCacheUnit.ts, value: { kind: 'loading' } };
        }
        else {
            staleAwareValue = { ts: currentCacheUnit.ts, value: { kind: 'unknown' } };
        }
    }
    return {
        override: data => {
            curRef.current = { kind: 'loaded', data };
            cacheSet(mkCacheUnit(curRef.current));
        },
        refresh: query,
        result: staleAwareValue.value,
    };
}
/**
 * For efficiently retrieving API data that's iteration based.
 * @param arg: Must be weakly "immutable". That is, the serialized value of it
 *   must not change.
 * @param nextArg: Must also be weakly immutable.
 * @param cacheGetSelector **IMPORTANT**: Assumes that this is a hook that will
 *   trigger a re-render of this hook if the value changes.
 * @param cacheTtl In seconds. If unset, ttl is infinite.
 * @param cacheIgnoreStale Set to true to ignore currently cached value.
 * @param argMutateOk Allowing the arg to mutate works but has edge cases and
 *   is fragile. See implementation for details.
 * @returns
 */
function useApiReadIterCache(apiClient, routeMethod, arg, routeMethodNext, nextArg, getValueFromResult, cacheSet, cacheGetSelector, disabled, handlers, cacheTtl, cacheIgnoreStale, argMutateOk) {
    const initTs = react_1.default.useRef(Date.now());
    const currentCacheUnit = cacheGetSelector();
    // This is necessary to know the instant value of the cache. The version in
    // the state variable can be stale when calling query() because query()'s
    // closure captures it.
    const curRef = react_1.default.useRef(currentCacheUnit.value);
    react_1.default.useEffect(() => {
        // Reflect external changes to cache state (data mutation / override) back
        // to instant cache state.
        if (ApiData.hasData(currentCacheUnit.value) && ApiData.hasData(curRef.current)) {
            curRef.current = currentCacheUnit.value;
        }
    }, [currentCacheUnit]);
    const apiClientAbortable = react_1.default.useRef();
    const serializedArg = JSON.stringify(arg);
    const serializedNextArg = JSON.stringify(nextArg);
    const query = (clearData) => {
        const argMutated = argSet.current && (argSet.current[0] !== serializedArg || argSet.current[1] !== serializedNextArg);
        if (ApiData.isLoading(curRef.current) && !argMutated) {
            return;
        }
        if (clearData) {
            curRef.current = { kind: 'loading' };
            cacheSet(mkCacheUnit(curRef.current, currentCacheUnit.ts));
        }
        else {
            if (!ApiData.isLoading(curRef.current)) {
                curRef.current = ApiData.toLoading(curRef.current);
                // If it's already loading, don't call toLoading() as that fails an
                // assertion. This should only be the case if the arg has been mutated.
                cacheSet(mkCacheUnit(curRef.current, currentCacheUnit.ts));
            }
        }
        apiClientAbortable.current = apiClient.abortable();
        routeMethod
            // The benefit of the late bind is that the method can be bound to an
            // abortable version of the client.
            .bind(apiClientAbortable.current)(arg)
            .then(resp => {
            // FIXME: Bug if an arg is mutated circularly from A -> B -> A.
            const postQueryArgMutated = argSet.current && (argSet.current[0] !== serializedArg || argSet.current[1] !== serializedNextArg);
            if (postQueryArgMutated) {
                return;
            }
            if (resp.kind === api.StatusCode.Ok) {
                // Call onResult first so that it's completed before the cache value
                // is set. This is important if the next render depends on state set
                // by onResult.
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onResult) {
                    handlers.onResult(resp.result);
                }
                curRef.current = {
                    data: [getValueFromResult(resp.result)],
                    kind: 'loaded',
                };
                cacheSet(mkCacheUnit(curRef.current));
            }
            else {
                curRef.current = { kind: 'error' };
                cacheSet(mkCacheUnit(curRef.current));
            }
        })
            .catch(e => {
            if (e.name !== 'AbortError') {
                curRef.current = { kind: 'error' };
                cacheSet(mkCacheUnit(curRef.current));
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onHttpErr) {
                    handlers.onHttpErr();
                }
            }
        });
        if (handlers === null || handlers === void 0 ? void 0 : handlers.onQuery) {
            handlers.onQuery();
        }
        return () => {
            apiClientAbortable.current.abort();
        };
    };
    const queryNext = () => {
        if (ApiData.isLoading(curRef.current)) {
            return;
        }
        if (!ApiData.hasData(curRef.current) || curRef.current.data.length === 0) {
            return;
        }
        const nextCursor = curRef.current.data[curRef.current.data.length - 1].cursor;
        if (!nextCursor) {
            return;
        }
        curRef.current = ApiData.toLoading(curRef.current);
        cacheSet(mkCacheUnit(curRef.current, currentCacheUnit.ts));
        apiClientAbortable.current = apiClient.abortable();
        routeMethodNext
            .bind(apiClientAbortable.current)(Object.assign(Object.assign({}, nextArg), { cursor: nextCursor }))
            .then(resp => {
            const postQueryArgMutated = argSet.current && (argSet.current[0] !== serializedArg || argSet.current[1] !== serializedNextArg);
            if (postQueryArgMutated) {
                return;
            }
            if (resp.kind === api.StatusCode.Ok) {
                // NOTE: The timestamp of the first iter is propagated rather than
                // updating the timestamp.
                if (ApiData.hasData(curRef.current)) {
                    curRef.current = {
                        data: curRef.current.data.concat(getValueFromResult(resp.result)),
                        kind: 'loaded',
                    };
                    cacheSet(mkCacheUnit(curRef.current));
                }
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onResult) {
                    handlers.onResult(resp.result);
                }
            }
            else {
                curRef.current = ApiData.toError(curRef.current);
                cacheSet(mkCacheUnit(curRef.current));
            }
        })
            .catch(e => {
            if (e.name !== 'AbortError') {
                curRef.current = ApiData.toError(curRef.current);
                cacheSet(mkCacheUnit(curRef.current));
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onHttpErr) {
                    handlers.onHttpErr();
                }
            }
        });
        return () => {
            apiClientAbortable.current.abort();
        };
    };
    react_1.default.useEffect(() => {
        if (disabled) {
            return;
        }
        else if (currentCacheUnit.value.kind === 'unknown' || isCacheExpired(currentCacheUnit, cacheTtl)) {
            query(false);
        }
    }, [disabled]);
    const argSet = react_1.default.useRef(null);
    react_1.default.useEffect(() => {
        if (disabled) {
            return;
        }
        // NOTE: react hot-reload re-invokes this even if args haven't changed,
        // which is why an explicit check against the known args is required.
        if (argSet.current === null) {
            argSet.current = [serializedArg, serializedNextArg];
        }
        else if (argSet.current[0] !== serializedArg || argSet.current[1] !== serializedNextArg) {
            if (argMutateOk) {
                query(false);
                // Set after query() so that query() can check for arg mutation.
                argSet.current = [serializedArg, serializedNextArg];
            }
            else {
                throw Error('Unexpected change to arg.');
            }
        }
    }, [serializedArg, serializedNextArg]);
    // This hides stale data if the caller requires it.
    let staleAwareValue = currentCacheUnit;
    if (cacheIgnoreStale && isCacheExpired(currentCacheUnit, cacheTtl) && currentCacheUnit.ts < initTs.current) {
        if (ApiData.isLoading(currentCacheUnit.value)) {
            staleAwareValue = { ts: currentCacheUnit.ts, value: { kind: 'loading' } };
        }
        else {
            staleAwareValue = { ts: currentCacheUnit.ts, value: { kind: 'unknown' } };
        }
    }
    const hasMore = ApiData.hasData(staleAwareValue.value) &&
        staleAwareValue.value.data.length > 0 &&
        staleAwareValue.value.data[staleAwareValue.value.data.length - 1].has_more;
    return {
        hasMore,
        next: () => queryNext(),
        refresh: (clear) => {
            query(clear !== null && clear !== void 0 ? clear : false);
        },
        result: staleAwareValue.value,
    };
}
function useApiReadIter(apiClient, routeMethod, arg, routeMethodNext, nextArg, getValueFromResult, disabled, handlers, argMutateOk) {
    const [value, setValue] = react_1.default.useState({ ts: 0, value: { kind: 'unknown' } });
    return useApiReadIterCache(apiClient, routeMethod, arg, routeMethodNext, nextArg, getValueFromResult, (newValue) => setValue(newValue), () => value, disabled, handlers, undefined, undefined, argMutateOk);
}
/**
 * Use this hook to implement a platform-specific useApiDo() hook.
 * FIXME: This is untested.
 */
function useApiDoBase(apiClient, routeMethod, opts) {
    const apiClientAbortable = react_1.default.useRef();
    react_1.default.useEffect(() => {
        return () => {
            if (apiClientAbortable.current && (opts === null || opts === void 0 ? void 0 : opts.abortable)) {
                apiClientAbortable.current.abort();
            }
        };
    }, []);
    const apiDo = (arg, handlers) => {
        apiClientAbortable.current = (opts === null || opts === void 0 ? void 0 : opts.abortable) ? apiClient.abortable() : apiClient;
        return (routeMethod
            // The benefit of the late bind is that the method can be bound to an
            // abortable version of the client.
            .bind(apiClientAbortable.current)(arg)
            .then(resp => {
            if (resp.kind === api.StatusCode.Ok) {
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onResult) {
                    handlers.onResult(resp.result);
                }
            }
            else if (resp.kind === api.StatusCode.Error) {
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onRouteErr) {
                    handlers.onRouteErr(resp.error);
                }
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onAnyErr) {
                    handlers.onAnyErr();
                }
            }
            else {
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onRespErr) {
                    handlers.onRespErr(resp);
                }
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onAnyErr) {
                    handlers.onAnyErr();
                }
            }
            return resp;
        })
            .catch(e => {
            if (e.name !== 'AbortError') {
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onHttpErr) {
                    handlers.onHttpErr();
                }
                if (handlers === null || handlers === void 0 ? void 0 : handlers.onAnyErr) {
                    handlers.onAnyErr();
                }
            }
            return e;
        })
            .finally(() => {
            if (handlers === null || handlers === void 0 ? void 0 : handlers.onFinally) {
                handlers.onFinally();
            }
        }));
    };
    return {
        apiDo,
    };
}
